import React, { useCallback, useMemo, useContext, useState } from 'react'
import useDaycareVisit from '../../../hooks/useDaycareVisit'
import useSelectedHousehold from '../../../hooks/useSelectedHousehold'
import SectionFormContainer from '../components/SectionFormContainer'
import DaycareWizardContext from './DaycareWizardContext'
import { makeStyles, CircularProgress, Button } from '@material-ui/core'
import { gray, purple } from '../../../utils/colors'
import {
	FONT_WEIGHTS,
	NON_BREAKING_SPACE,
	formatNumsToDollars,
	capitilizeMultipleWords,
	calculatePromoAmountToApply,
	getPromoApplicationFlags,
	PROMO_SERVICE_TARGETS,
	LINE_ITEM_TYPES,
	DAYCARE,
	getApplicableAmountToApplyToLineItem
} from '../../../utils'
import { ButtonBase, MenuItem } from '@material-ui/core'
import { MainButton } from '../../Button/MainButton'
import PaymentMethodCard from '../../Cards/Payments/PaymentMethodCard'
import CancellationPolicyDialog from './components/CancellationPolicyDialog'
import { showError } from '../../../actions/misc/misc.actions'
import MoreMenu from '../../Common/Menus/MoreMenu'
import EditAddOnsDialog from './components/EditAddOnsDialog'
import EditMealAddOnsDialog from './components/EditMealAddOnsDialog'
import RemoveEarlyDropoffDialog from './components/RemoveEarlyDropoffDialog'
import RemoveLatePickupDialog from './components/RemoveLatePickupDialog'
import PromoOnlyEntryDialog from '../../Dialogs/Promos/PromoOnlyEntryDialog'
import usePromotions from '../../../hooks/usePromotions'
import * as Constants from '../../../utils/constants'
import StyledAlert from '../../Common/StyledAlert'
import NoFacilityCardWarning from '../../Common/NoFacilityCardWarning'
import DifferentFacilityBookingWarning from '../../Common/DifferentFacilityBookingWarning'
import useFacilityPaymentStatus from '../../../hooks/useFacilityPaymentStatus'

const useStyles = makeStyles(() => ({
	root: {
		flex: 1,
		width: '100%',
		display: 'flex',
		flexDirection: 'row',
		alignSelf: 'center',
		gap: '1rem',
		padding: '0 1rem',
		position: 'relative'
	},
	invoiceContainer: {
		display: 'flex',
		backgroundColor: gray[300],
		width: '70%',
		borderRadius: '0.375rem',
		flexDirection: 'column',
		padding: '1.4rem'
	},
	invoiceHeader: {
		marginBottom: '1.5rem'
	},
	emphasizedText: {
		fontFamily: 'Nunito',
		fontWeight: FONT_WEIGHTS.ExtraBold,
		fontSize: '1rem',
		color: gray[900]
	},
	mealText: {
		fontSize: '.875rem',
		color: gray[700],
		fontStyle: 'italic'
	},
	actionsContainer: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column',
		'& .MuiCard-root': {
			boxShadow: 'none',
			'& .MuiCardHeader-root': {
				'& .MuiCardHeader-content': {
					'& .MuiCardHeader-title': {
						textTransform: 'unset'
					}
				}
			}
		}
	},
	actionButtonContainer: {
		display: 'flex',
		flexDirection: 'column',
		padding: '1rem',
		gap: '1rem'
	},
	disclaimerText: {
		textAlign: 'center',
		fontFamily: 'Nunito',
		fontSize: '0.875rem',
		padding: '0 0.5rem',
		fontWeight: FONT_WEIGHTS.SemiBold,
		'& span': {
			fontWeight: FONT_WEIGHTS.ExtraBold,
			color: purple[500],
			cursor: 'pointer'
		}
	},
	activityOverlay: {
		position: 'absolute',
		zIndex: 200,
		backgroundColor: 'rgba(255,255,255,.8)',
		width: 'calc(100% - 2rem)',
		height: '100%',
		display: 'flex',
		flex: 1,
		alignItems: 'center',
		justifyContent: 'center'
	},
	divider: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		marginBottom: '1rem',
		'& div:nth-child(1)': {
			height: '1px',
			borderBottom: `1px solid ${gray[500]}`,
			flex: 1
		}
	},
	spacer: {
		flex: 1
	},
	inlineButtons: {
		fontFamily: 'Nunito',
		fontSize: '0.875rem',
		fontWeight: FONT_WEIGHTS.ExtraBold,
		color: purple[500]
	},
	addPromoButton: {
		color: purple[500],
		fontSize: '0.875rem',
		letterSpacing: 1.25,
		padding: 0,
		marginTop: '1.25rem'
	}
}))

const useRowStyles = makeStyles(() => ({
	row: {
		display: 'flex',
		flex: 1,
		gap: '.875rem',
		alignItems: 'center',
		fontFamily: 'Nunito',
		fontWeight: FONT_WEIGHTS.SemiBold,
		fontSize: '1rem',
		color: gray[900],
		width: '100%',
		'& div:nth-child(1)': {
			width: '8.5rem'
		},
		'& div:nth-child(2)': {
			width: '1.6rem'
		},
		'& div:nth-child(3)': {
			width: '5.2125rem'
		},
		'& div:nth-child(4)': {
			width: '5.3rem'
		},
		'& div:nth-child(5)': {
			width: '4.7rem'
		},
		'& div:nth-child(6)': {
			width: '1.5rem'
		}
	},
	header: {
		borderBottom: `1px solid ${gray[500]}`,
		height: '2rem',
		fontWeight: FONT_WEIGHTS.SemiBold,
		fontSize: '0.875rem',
		flex: 'unset',
		marginBottom: '0.3125rem'
	},
	table: {
		display: 'flex',
		flexDirection: 'column',
		gap: '.6rem'
	}
}))

const useTableStyles = ({ scrollable }) =>
	makeStyles(() => {
		const styles = {
			table: {
				display: 'flex',
				flexDirection: 'column',
				gap: '.6rem'
			}
		}
		if (scrollable) {
			styles.table['overflow'] = 'scroll'
			styles.table['maxHeight'] = '32rem'
			styles.table['marginBottom'] = '1rem'
		}
		return styles
	})

const Row = ({ children, header = false }) => {
	const classes = useRowStyles()
	return <div className={`${classes.row} ${header ? classes.header : null}`}>{children}</div>
}

const Header = ({ children }) => {
	return <Row header>{children}</Row>
}

const FakeTable = ({ children, scrollable }) => {
	const classes = useTableStyles({ scrollable })()
	return <div className={classes.table}>{children}</div>
}

const ServiceSummaryAndPayment = (props) => {
	const {
		wizardContextProvider: { wizard_state, setWizardAttributeAndValues }
	} = useContext(DaycareWizardContext)
	const classes = useStyles()
	const { facility, daycare, updateAddOns, update, prices, updating } = useDaycareVisit()
	const {
		addOnBankValue,
		hasValidCardOnFile,
		household,
		daycareCredits,
		isVip,
		isPetCare,
		totalCreditAvailable
	} = useSelectedHousehold()

	// Cards are saved per facility. The wizard creates a Stripe customer for this
	// facility and will not confirm until a card exists on that facility's instance.
	const facilityCardStatus = useFacilityPaymentStatus(household?._id, facility?._id, { ensureCustomer: true })
	const hasCardAtThisFacility =
		facilityCardStatus.hasPaymentMethod === true ||
		(facilityCardStatus.hasPaymentMethod == null &&
			String(facility?._id || '') === String(household?.default_facility_id || '') &&
			hasValidCardOnFile)
	const { deletePromoForFacilityVisit } = usePromotions()

	const [cancellationPolicyOpen, setCancellationPolicyOpen] = useState(false)
	const [editItemsDialogOpen, setEditItemsDialogOpen] = useState(false)
	const [editMealAddOnsDialogOpen, setEditMealAddOnsDialogOpen] = useState(false)
	const [removeEarlyDropoffDialogOpen, setRemoveEarlyDropoffDialogOpen] = useState(false)
	const [removeLatePickupDialogOpen, setRemoveLatePickupDialogOpen] = useState(false)
	const [promoEntryDialogOpen, setPromoEntryDialogOpen] = useState(false)
	const [addonIdToEdit, setAddonIdToEdit] = useState('')
	const [addonChoiceIdToEdit, setAddonChoiceIdToEdit] = useState('')
	const [editAddOnTitle, setEditAddonTitle] = useState('')
	const [foodAddOnsToEdit, setFoodAddOnsToEdit] = useState({
		pet_id: '',
		meals: [],
		name: '',
		image_url: ''
	})
	const [showBankCreditDisclaimer, setShowBankCreditDisclaimer] = useState(false)

	const handleCancellationPolicyDialogState = useCallback(() => {
		setCancellationPolicyOpen((prev) => !prev)
	}, [setCancellationPolicyOpen])

	const numberOfPets = useMemo(() => wizard_state['pets'].length, [wizard_state])
	const visitId = useMemo(() => wizard_state['_id'], [wizard_state])

	const nonMealAddons = useMemo(() => {
		const { add_on_services } = wizard_state
		return add_on_services.filter((addon) => addon.category !== 'meal')
	}, [facility, wizard_state])

	const creditsApplied = useMemo(() => {
		let available = 0
		const credits = daycareCredits.filter((c) => !c.expired && !c.used && c.credit_remaining > 0)

		if (credits.length) {
			available = credits.reduce((acc, cred) => acc + cred.credit_remaining, 0)
		}

		if (available > numberOfPets) {
			return numberOfPets
		}
		return available
	}, [wizard_state, numberOfPets, daycareCredits])

	const hasMorningMeal = useMemo(() => {
		const { add_on_services } = wizard_state

		// There maybe more than one morning meal for multiple pets, but we only need to find one
		// to know that morning meals have been selected
		const meal = add_on_services.some((addon) => addon.category === 'meal' && addon.time === 'morning')

		return meal || false
	}, [wizard_state])

	const hasEveningMeal = useMemo(() => {
		const { add_on_services } = wizard_state

		// There maybe more than one evening meal for multiple pets, but we only need to find one
		// to know that evening meals have been selected
		const meal = add_on_services.some((addon) => addon.category === 'meal' && addon.time === 'evening')

		return meal || false
	}, [wizard_state])

	const handleRemoveEarlyDropoffDialogState = useCallback(() => {
		setRemoveEarlyDropoffDialogOpen((prev) => !prev)
	}, [setRemoveEarlyDropoffDialogOpen])

	const handleRemoveLatePickupDialogState = useCallback(() => {
		setRemoveLatePickupDialogOpen((prev) => !prev)
	}, [setRemoveLatePickupDialogOpen])

	const handlePromoEntryDialogState = useCallback(() => {
		setPromoEntryDialogOpen((prev) => !prev)
	}, [setPromoEntryDialogOpen])

	const handleRemoveEarlyDropoff = useCallback(async () => {
		handleRemoveEarlyDropoffDialogState()
		const newValues = { ...wizard_state }
		const { pets = [] } = wizard_state
		newValues['early_dropoff'] = false

		if (hasMorningMeal) {
			const { add_on_services } = wizard_state
			const newAddOnArray = add_on_services.filter((addon) => {
				if (addon.category === 'meal' && addon.time === 'morning') {
					return false
				}
				return true
			})

			newValues['add_on_services'] = newAddOnArray
		}

		setWizardAttributeAndValues(newValues)
		if (hasMorningMeal) {
			const petIds = pets.map((a) => a._id)
			await updateAddOns({ visitId, addons: newValues['add_on_services'], petIds })
		}
		await update({ visitId, updates: { early_dropoff: false } })
	}, [
		wizard_state,
		setWizardAttributeAndValues,
		update,
		visitId,
		hasMorningMeal,
		handleRemoveEarlyDropoffDialogState,
		updateAddOns
	])

	const handleRemoveLatePickup = useCallback(async () => {
		handleRemoveLatePickupDialogState()
		const newValues = { ...wizard_state }
		const { pets = [] } = wizard_state
		newValues['extended_pickup'] = false

		if (hasEveningMeal) {
			const { add_on_services } = wizard_state
			const newAddOnArray = add_on_services.filter((addon) => {
				if (addon.category === 'meal' && addon.time === 'evening') {
					return false
				}
				return true
			})

			newValues['add_on_services'] = newAddOnArray
		}

		setWizardAttributeAndValues(newValues)
		if (hasEveningMeal) {
			const petIds = pets.map((a) => a._id)
			await updateAddOns({ visitId, addons: newValues['add_on_services'], petIds })
		}
		await update({ visitId, updates: { extended_pickup: false } })
	}, [
		wizard_state,
		setWizardAttributeAndValues,
		update,
		visitId,
		hasEveningMeal,
		handleRemoveLatePickupDialogState,
		updateAddOns
	])

	const handleRemovePromo = useCallback(async () => {
		const { applied_promo, _id, household_id } = wizard_state
		if (!applied_promo) {
			return
		}

		await deletePromoForFacilityVisit({
			promo_code: applied_promo.code,
			promo_id: applied_promo._id,
			visit_id: _id,
			household_id
		})
	}, [deletePromoForFacilityVisit, wizard_state])

	const handleEditAddOns = useCallback(
		({ addon_id, addon_choice_id = '' }, title) => {
			setEditItemsDialogOpen(true)
			setAddonIdToEdit(addon_id)
			setAddonChoiceIdToEdit(addon_choice_id)
			setEditAddonTitle(title)
		},
		[setEditItemsDialogOpen, setAddonIdToEdit, setAddonChoiceIdToEdit]
	)

	const handleEditMealAddons = useCallback(
		({ pet_id, meals, name, image_url }) => {
			setFoodAddOnsToEdit({ pet_id, meals, name, image_url })
			setEditMealAddOnsDialogOpen(true)
		},
		[setFoodAddOnsToEdit]
	)

	const handleRemoveAddOn = useCallback(
		async ({ addon, forAll = false }) => {
			const newValues = { ...wizard_state }
			const { pets = [] } = wizard_state
			let newAddOnArray = []

			if (!addon && forAll) {
				setEditItemsDialogOpen(false)
				newAddOnArray = newValues['add_on_services'].filter((a) => {
					if (a.category === 'meal') {
						return true
					}
					if (a.addon_id === addonIdToEdit) {
						if (addonChoiceIdToEdit > '' && a.addon_choice_id !== addonChoiceIdToEdit) {
							return true
						}
						return false
					}
					return true
				})
			} else {
				// Not implemented yet
			}

			newValues['add_on_services'] = newAddOnArray
			setWizardAttributeAndValues(newValues)
			const petIds = pets.map((a) => a._id)
			await updateAddOns({ visitId, addons: newValues['add_on_services'], petIds })

			setAddonIdToEdit('')
			setAddonChoiceIdToEdit('')
		},
		[
			wizard_state,
			setWizardAttributeAndValues,
			addonChoiceIdToEdit,
			addonIdToEdit,
			updateAddOns,
			visitId,
			setAddonIdToEdit,
			setAddonChoiceIdToEdit
		]
	)

	const handleUpdateFoodAddons = useCallback(
		async ({ pet_id, meals, exceptions }) => {
			setEditMealAddOnsDialogOpen(false)
			const { add_on_services, pets = [] } = wizard_state
			// remove existing meals for this pet from addons
			const newAddOnArray = add_on_services.filter((addon) => {
				const { category, pet_id: addonPetId } = addon
				if (category !== 'meal') {
					return true
				}

				if (addonPetId !== pet_id) {
					return true
				}

				return false
			})

			// add them back in and then save to db.
			for (const time of meals) {
				newAddOnArray.push({ category: 'meal', time, pet_id })
			}

			const newValues = { ...wizard_state }
			newValues['add_on_services'] = newAddOnArray
			setWizardAttributeAndValues(newValues)
			const petIds = pets.map((a) => a._id)
			await updateAddOns({ visitId, addons: newValues['add_on_services'], petIds })
			setFoodAddOnsToEdit({
				pet_id: '',
				meals: [],
				name: '',
				image_url: ''
			})
		},
		[
			wizard_state,
			setWizardAttributeAndValues,
			setEditMealAddOnsDialogOpen,
			setFoodAddOnsToEdit,
			visitId,
			updateAddOns
		]
	)

	const handleUpdateAddons = useCallback(
		async (revisedAddons) => {
			setEditItemsDialogOpen(false)
			const newValues = { ...wizard_state }
			const { pets = [] } = wizard_state
			newValues['add_on_services'] = [...revisedAddons]
			setWizardAttributeAndValues(newValues)
			const petIds = pets.map((a) => a._id)
			await updateAddOns({ visitId, addons: newValues['add_on_services'], petIds })

			setAddonIdToEdit('')
			setAddonChoiceIdToEdit('')
		},
		[
			wizard_state,
			setWizardAttributeAndValues,
			setEditItemsDialogOpen,
			setAddonIdToEdit,
			setAddonChoiceIdToEdit,
			visitId,
			updateAddOns
		]
	)

	const serviceCost = useMemo(() => {
		let price = -1

		if (prices) {
			const cost = prices['1']
			price = cost
		}
		return price
	}, [prices])

	const serviceLineItem = useMemo(() => {
		const lineItems = []

		if (creditsApplied < numberOfPets) {
			let nonCoveredPets = numberOfPets - creditsApplied
			const nonCoveredCost = serviceCost * nonCoveredPets
			lineItems.push({
				description: 'Daycare Sessions',
				quantity: nonCoveredPets,
				upcharge: formatNumsToDollars(0),
				price: nonCoveredCost,
				totalPrice: nonCoveredCost,
				formattedPrice: formatNumsToDollars(nonCoveredCost),
				formattedQuantity: `${nonCoveredPets}x`,
				formattedTotalPrice: formatNumsToDollars(nonCoveredCost),
				formattedUpcharge: formatNumsToDollars(0)
			})
		}

		if (creditsApplied > 0) {
			const coveredCost = serviceCost * creditsApplied
			lineItems.push({
				description: 'Daycare Sessions',
				quantity: creditsApplied,
				upcharge: 0,
				price: 0,
				totalPrice: coveredCost,
				formattedPrice: `${creditsApplied} credits`,
				formattedQuantity: `${creditsApplied}x`,
				formattedTotalPrice: formatNumsToDollars(coveredCost),
				formattedUpcharge: formatNumsToDollars(0)
			})
		}

		return lineItems
	}, [serviceCost, creditsApplied, numberOfPets])

	const creditBankLineItem = useMemo(() => {
		const totalPrice = serviceCost * creditsApplied
		return [
			{
				description: 'Credit Bank',
				quantity: '',
				upcharge: formatNumsToDollars(0),
				price: `-${creditsApplied} credits`,
				totalPrice: -totalPrice,
				formattedUpcharge: formatNumsToDollars(0),
				formattedTotalPrice: `-${formatNumsToDollars(totalPrice)}`,
				formattedPrice: `-${creditsApplied} credits`
			}
		]
	}, [serviceCost, creditsApplied, numberOfPets])

	const addOnlineItems = useMemo(() => {
		const addOnMap = new Map()
		const { pets } = wizard_state

		if (facility) {
			const { addOnTypes } = facility || []

			nonMealAddons.forEach((addon, i) => {
				let price = 0
				let upcharge = 0
				let suboptionDescription = ''
				let currentPet = pets.find((pet) => pet._id === addon.pet_id)
				const {
					name,
					_id: rawId,
					addon_id: existingAddOnId,
					addon_choice_id = '',
					pet_id,
					training_service
				} = addon

				const addonKey = `${existingAddOnId || rawId}${addon_choice_id}`

				const masterAddon = addOnTypes.find(({ _id }) => {
					return _id === existingAddOnId || _id === rawId
				})

				if (masterAddon) {
					price = masterAddon.price
					if (addon_choice_id > '' && masterAddon.addon_choices) {
						const { upcharge: selectionUpcharge, name: selectionName } =
							masterAddon.addon_choices.find((choice) => {
								return choice.id === addon_choice_id
							}) || { upcharge: 0, name: '' }

						upcharge = selectionUpcharge
						suboptionDescription = selectionName
					}
				}

				const lineItem = {
					description: `${name}${suboptionDescription > '' ? ` - ${suboptionDescription}` : ''}`
				}

				if (!addOnMap.has(addonKey)) {
					const totalPrice = price + upcharge
					const formattedQuantity = `1x`
					const formattedPrice = price > -1 ? `${formatNumsToDollars(price)}` : '??'
					const formattedTotalPrice = totalPrice > -1 ? `${formatNumsToDollars(totalPrice)}` : '??'
					const formattedUpcharge = upcharge > -1 ? `${formatNumsToDollars(upcharge)}` : '??'

					const item = {
						...lineItem,
						quantity: 1,
						price,
						upcharge,
						totalPrice,
						formattedQuantity,
						formattedPrice,
						formattedUpcharge,
						formattedTotalPrice,
						training_service,
						options: [
							<MenuItem onClick={() => handleEditAddOns(addon, lineItem.description)}>Edit</MenuItem>
						]
					}

					addOnMap.set(addonKey, item)
				} else {
					const updatedItem = { ...addOnMap.get(addonKey) }
					const newQuantity = updatedItem.quantity + 1
					const newFormattedQuantity = `${newQuantity}x`
					const newPrice = updatedItem.price * newQuantity
					const newUpcharge = updatedItem.upcharge * newQuantity
					const newTotalPrice = newPrice + newUpcharge
					const newFormattedPrice = newTotalPrice > -1 ? `${formatNumsToDollars(newTotalPrice)}` : '??'

					updatedItem.quantity = newQuantity
					updatedItem.totalPrice = newTotalPrice
					updatedItem.formattedTotalPrice = newFormattedPrice
					updatedItem.formattedQuantity = newFormattedQuantity

					addOnMap.set(addonKey, updatedItem)
				}
			})
		}

		return [...addOnMap.values()]
	}, [wizard_state, nonMealAddons, facility])

	const pickupDropoffLineItems = useMemo(() => {
		const { early_dropoff, extended_pickup } = wizard_state
		const { early_dropoff_fee } = daycare || { early_dropoff_fee: 0, late_pickup_fee: 0 }
		const earlyDropoffPrice = isVip || isPetCare ? 0 : early_dropoff_fee
		const latePickupPrice = 0

		const lineItems = []

		if (early_dropoff) {
			lineItems.push({
				description: 'Early Drop-off',
				quantity: 1,
				upcharge: 0,
				price: earlyDropoffPrice,
				totalPrice: earlyDropoffPrice,
				formattedQuantity: '1x',
				formattedUpcharge: formatNumsToDollars(0),
				formattedTotalPrice: formatNumsToDollars(earlyDropoffPrice),
				formattedPrice: formatNumsToDollars(earlyDropoffPrice),
				options: [<MenuItem onClick={handleRemoveEarlyDropoffDialogState}>Remove</MenuItem>]
			})
		}
		if (extended_pickup) {
			lineItems.push({
				description: 'Late Pick-up',
				quantity: 1,
				upcharge: 0,
				price: latePickupPrice,
				totalPrice: latePickupPrice,
				formattedQuantity: '1x',
				formattedUpcharge: formatNumsToDollars(0),
				formattedTotalPrice: formatNumsToDollars(latePickupPrice),
				formattedPrice: formatNumsToDollars(latePickupPrice),
				options: [<MenuItem onClick={handleRemoveLatePickupDialogState}>Remove</MenuItem>]
			})
		}

		return lineItems
	}, [wizard_state, daycare, isVip, isPetCare])

	const addOnValue = useMemo(() => {
		return addOnlineItems.reduce((acc, item) => {
			return acc + item.price
		}, 0)
	}, [addOnlineItems])

	const foodScheduleLineItems = useMemo(() => {
		const { add_on_services, pets = [] } = wizard_state
		const mealAddOns = add_on_services.filter((addon) => addon.category === 'meal')

		const lineItems = []

		const mealsMap = new Map()

		pets.forEach((pet) => {
			mealsMap.set(pet._id, {
				pet_id: pet._id,
				name: pet.name,
				image_url: pet.image_url,
				meals: []
			})
		})

		mealAddOns.forEach((addon) => {
			const { pet_id, time } = addon
			if (mealsMap.has(pet_id)) {
				const meals = mealsMap.get(pet_id)
				meals.meals.push(time)
				mealsMap.set(pet_id, meals)
			}
		})

		const formattedZero = formatNumsToDollars(0)

		mealsMap.forEach(({ pet_id, name, meals, image_url }, _) => {
			lineItems.push({
				quantity: meals.length,
				description: (
					<div>
						Food Schedule - {name}
						<br />
						<span className={classes.mealText}>
							{meals.length > 0
								? capitilizeMultipleWords(meals.join('-'), '-', ', ')
								: '[No Meals Selected]'}
						</span>
					</div>
				),
				tooltipDescription: `Food Schedule - ${name}`,
				price: 0,
				upcharge: 0,
				totalPrice: 0,
				formattedQuantity: `${meals.length}x`,
				formattedPrice: formattedZero,
				formattedUpcharge: formattedZero,
				formattedTotalPrice: formattedZero,
				options: [
					<MenuItem onClick={() => handleEditMealAddons({ pet_id, meals, name, image_url })}>
						Edit
					</MenuItem>
				]
			})
		})

		return lineItems
	}, [wizard_state, nonMealAddons, facility])

	const totalPrice = useMemo(() => {
		let subtotal = 0
		for (const item of serviceLineItem) {
			subtotal += item.totalPrice
		}
		for (const item of addOnlineItems) {
			subtotal += item.totalPrice
		}
		for (const item of pickupDropoffLineItems) {
			subtotal += item.totalPrice
		}
		for (const item of creditBankLineItem) {
			subtotal += item.totalPrice
		}
		return subtotal
	}, [serviceLineItem, addOnlineItems, pickupDropoffLineItems, creditBankLineItem])

	const appliedPromoAndAddonBankLineItems = useMemo(() => {
		const { applied_promo } = wizard_state

		const lineItems = []

		if (!applied_promo && addOnBankValue <= 0) {
			return lineItems
		}

		let remainingAddonBalance = addOnValue
		let availableAddonBank = addOnBankValue || 0
		let remainingAddonBank = addOnBankValue || 0
		let addonBankAppliedInCents = 0
		let amountApplicableToPromo = 0

		if (applied_promo) {
			const { code, value, unit } = applied_promo || {}
			const {
				appliesToDaycare,
				appliesToCredits,
				appliesToTraining,
				appliesToFVRT,
				appliesToVisitAddons
			} = getPromoApplicationFlags(applied_promo, DAYCARE)

			const appliedPromoLineItem = {
				description: (
					<div>
						Applied Promo
						<br />
						<span className={classes.mealText}>{code}</span>
					</div>
				),
				tooltipDescription: code,
				quantity: 1,
				upcharge: 0,
				price: 0,
				totalPrice: 0,
				formattedQuantity: '',
				formattedUpcharge: '',
				formattedPrice: '',
				formattedTotalPrice: `-${formatNumsToDollars(0)}`,
				options: [
					<MenuItem key="edit-promo" onClick={handlePromoEntryDialogState}>
						Edit
					</MenuItem>,
					<MenuItem key="remove-promo" onClick={handleRemovePromo}>
						Remove
					</MenuItem>
				]
			}
			let daycareTotal = serviceLineItem.reduce((acc, i) => acc + i.totalPrice, 0)
			let pickupDropoffTotal = pickupDropoffLineItems.reduce((acc, i) => acc + i.totalPrice, 0)
			let creditsTotal = 0

			let promoAmountAppliedToAddOnsInCents = 0

			// Need to handle $ based promos
			const promoIsCentBased = unit === Constants.CENTS
			const promoValue = value || 0

			let remainingPromoAmountInCents = promoIsCentBased ? promoValue : -1 // -1 helps us distinguish between $ and % based promos in the calculation logic

			if (creditBankLineItem.length > 0) {
				creditsTotal = creditBankLineItem.reduce((acc, i) => acc + i.totalPrice, 0)
			}

			if (appliesToCredits) {
				amountApplicableToPromo = daycareTotal + pickupDropoffTotal + creditsTotal
			} else if (appliesToDaycare) {
				amountApplicableToPromo = pickupDropoffTotal
			}

			const amountToApply = calculatePromoAmountToApply({
				promo: applied_promo,
				total: amountApplicableToPromo,
				visit: wizard_state,
				remainingPromoAmountInCents
			})

			promoAmountAppliedToAddOnsInCents += -amountToApply
			if (promoIsCentBased) {
				remainingPromoAmountInCents += amountToApply
			}

			appliedPromoLineItem.price = promoAmountAppliedToAddOnsInCents * -1
			appliedPromoLineItem.totalPrice = promoAmountAppliedToAddOnsInCents * -1
			appliedPromoLineItem.formattedTotalPrice = `-${formatNumsToDollars(promoAmountAppliedToAddOnsInCents)}`
			appliedPromoLineItem.subtext = applied_promo.code
			appliedPromoLineItem.type = LINE_ITEM_TYPES.PreLineItemAppliedPromo

			if (addOnlineItems.length > 0 && appliesToVisitAddons) {
				for (const addon of addOnlineItems) {
						if (!addon.training_service && (appliesToFVRT || appliesToTraining)) {
							continue
						}

						if (promoIsCentBased) {
							if (promoValue > 0) {
								if (remainingPromoAmountInCents > 0) {
									const applicableAmount = getApplicableAmountToApplyToLineItem(
										addon.totalPrice,
										remainingPromoAmountInCents,
										promoAmountAppliedToAddOnsInCents
									)

									remainingAddonBalance -= applicableAmount.individualAmountApplied
									remainingPromoAmountInCents = applicableAmount.remainingBalance
									promoAmountAppliedToAddOnsInCents = applicableAmount.runningAmountApplied
								}
							}
						} else {
							const percentToInt = parseInt(promoValue, 10)
							const subtractedAmount = addon.totalPrice * (percentToInt / 100)
							remainingAddonBalance -= subtractedAmount
							promoAmountAppliedToAddOnsInCents += subtractedAmount
							remainingPromoAmountInCents = -1
						}
					}

				appliedPromoLineItem.price = promoAmountAppliedToAddOnsInCents * -1
				appliedPromoLineItem.totalPrice = promoAmountAppliedToAddOnsInCents * -1
				appliedPromoLineItem.formattedTotalPrice = `-${formatNumsToDollars(
					promoAmountAppliedToAddOnsInCents
				)}`
			}

			lineItems.push(appliedPromoLineItem)
		}

		const addonBankLineItem = {
			description: 'Enrichment Bank',
			quantity: 1,
			upcharge: 0,
			price: 0,
			totalPrice: 0,
			formattedQuantity: '',
			formattedUpcharge: '',
			formattedPrice: '',
			formattedTotalPrice: `-${formatNumsToDollars(0)}`
		}

		if (availableAddonBank > 0 && remainingAddonBalance > 0 && addOnlineItems.length > 0) {
			const applicableBankAmount = getApplicableAmountToApplyToLineItem(
				remainingAddonBalance,
				remainingAddonBank,
				addonBankAppliedInCents
			)
			remainingAddonBalance -= applicableBankAmount.individualAmountApplied
			remainingAddonBank = applicableBankAmount.remainingBalance
			addonBankAppliedInCents = applicableBankAmount.runningAmountApplied

			if (addonBankAppliedInCents > 0) {
				addonBankLineItem.price = addonBankAppliedInCents * -1
				addonBankLineItem.totalPrice = addonBankAppliedInCents * -1
				addonBankLineItem.formattedTotalPrice = `-${formatNumsToDollars(addonBankAppliedInCents)}`
				lineItems.push(addonBankLineItem)
				setShowBankCreditDisclaimer(true)
			}
		}

		return lineItems
	}, [
		wizard_state,
		totalPrice,
		serviceLineItem,
		creditBankLineItem,
		addOnlineItems,
		addOnValue,
		pickupDropoffLineItems,
		addOnBankValue,
		setShowBankCreditDisclaimer
	])

	const availableAccountCreditLineItems = useMemo(() => {
		if (totalCreditAvailable <= 0) {
			return []
		}

		let total = totalPrice

		if (total <= 0) {
			return []
		}

		const items = []

		for (const item of appliedPromoAndAddonBankLineItems) {
			total += item.price
		}

		let price = total - totalCreditAvailable

		if (totalCreditAvailable > total || totalCreditAvailable === total) {
			price = total
		} else if (totalCreditAvailable < total) {
			price = totalCreditAvailable
		}

		setShowBankCreditDisclaimer(true)

		items.push({
			description: 'Account Credit',
			quantity: '',
			upcharge: formatNumsToDollars(0),
			price: `-${price} credits`,
			totalPrice: -price,
			formattedUpcharge: formatNumsToDollars(0),
			formattedTotalPrice: `-${formatNumsToDollars(price)}`,
			formattedPrice: `-${formatNumsToDollars(price)}`
		})

		return items
	}, [totalCreditAvailable, totalPrice, appliedPromoAndAddonBankLineItems, setShowBankCreditDisclaimer])

	const totalPriceWithPromo = useMemo(() => {
		let subtotal = totalPrice
		for (const item of appliedPromoAndAddonBankLineItems) {
			subtotal += item.totalPrice
		}
		for (const item of availableAccountCreditLineItems) {
			subtotal += item.totalPrice
		}
		return subtotal
	}, [totalPrice, wizard_state, appliedPromoAndAddonBankLineItems, availableAccountCreditLineItems])

	const handleOnConfirmAndBook = useCallback(async () => {
		const newValues = { ...wizard_state }
		const approvedStatus = { id: 2, name: 'Approved' }
		newValues['status'] = approvedStatus
		setWizardAttributeAndValues(newValues)

		try {
			const response = await update({
				visitId,
				updates: { status: approvedStatus, require_facility_payment_method: true }
			})
			if (response && response.data) {
				const { data } = response
				if (data.visit && props.onComplete) {
					props.onComplete()
				}
			}
		} catch (e) {
			showError('Error Scheduling!')
			console.log('error scheduling', e)
		}
	}, [wizard_state, update, visitId])

	const promoServiceTargets = useMemo(() => {
		const { type, add_on_services } = wizard_state
		if (!add_on_services || add_on_services.length === 0) {
			return [type]
		}
		const hasTraining = add_on_services.some((addon) => addon.training_service)
		if (hasTraining) {
			return [type, PROMO_SERVICE_TARGETS.TRAINING, PROMO_SERVICE_TARGETS.FVRT]
		}
	}, [wizard_state])

	return (
		<SectionFormContainer childrenContainerStyles={{ margin: '1rem 0' }}>
			<CancellationPolicyDialog
				open={cancellationPolicyOpen}
				onClose={handleCancellationPolicyDialogState}
				numPets={numberOfPets}
			/>
			<EditMealAddOnsDialog
				open={editMealAddOnsDialogOpen}
				onCancel={() => setEditMealAddOnsDialogOpen(false)}
				values={foodAddOnsToEdit}
				onSave={(updates) => handleUpdateFoodAddons(updates)}
			/>
			<EditAddOnsDialog
				open={editItemsDialogOpen}
				title={editAddOnTitle}
				onDelete={() => handleRemoveAddOn({ forAll: true })}
				onSave={(revisedAddons) => handleUpdateAddons(revisedAddons)}
				onCancel={() => setEditItemsDialogOpen(false)}
				addon_id={addonIdToEdit}
				addon_choice_id={addonChoiceIdToEdit}
			/>
			<RemoveEarlyDropoffDialog
				open={removeEarlyDropoffDialogOpen}
				onCancel={handleRemoveEarlyDropoffDialogState}
				onDelete={handleRemoveEarlyDropoff}
				hasMorningMeal={hasMorningMeal}
				standardDropoffHours={daycare?.standard_dropoff_hours || ''}
			/>
			<RemoveLatePickupDialog
				open={removeLatePickupDialogOpen}
				onCancel={handleRemoveLatePickupDialogState}
				onDelete={handleRemoveLatePickup}
				hasEveningMeal={hasEveningMeal}
				standardPickupTime={daycare?.standard_pickup_time || ''}
			/>
			<PromoOnlyEntryDialog
				open={promoEntryDialogOpen}
				onCancel={handlePromoEntryDialogState}
				onComplete={handlePromoEntryDialogState}
				visit={wizard_state}
				service_targets={promoServiceTargets}
			/>
			<div className={classes.root}>
				{updating ? (
					<div className={classes.activityOverlay}>
						<CircularProgress />
					</div>
				) : null}
				<div className={classes.invoiceContainer}>
					<div className={`${classes.invoiceHeader} ${classes.emphasizedText}`}>
						Daycare Appointment Details
					</div>
					<FakeTable scrollable>
						<Header>
							<div>ITEM</div>
							<div>QTY</div>
							<div>PRICE</div>
							<div>UPCHARGE</div>
							<div>AMOUNT</div>
							<div>{NON_BREAKING_SPACE}</div>
						</Header>
						{[
							...serviceLineItem,
							...creditBankLineItem,
							...addOnlineItems,
							...pickupDropoffLineItems,
							...foodScheduleLineItems,
							...appliedPromoAndAddonBankLineItems,
							...availableAccountCreditLineItems
						].map(
							({
								description,
								formattedQuantity,
								formattedPrice,
								formattedUpcharge,
								formattedTotalPrice,
								options,
								tooltipDescription
							}) => {
								return (
									<Row>
										<div>{description}</div>
										<div>{formattedQuantity}</div>
										<div>{formattedPrice}</div>
										<div>{formattedUpcharge}</div>
										<div>{formattedTotalPrice}</div>
										{options ? (
											<MoreMenu
												description={tooltipDescription || description}
												options={options}
											/>
										) : (
											<div>{NON_BREAKING_SPACE}</div>
										)}
									</Row>
								)
							}
						)}
					</FakeTable>
					<div className={classes.spacer} />
					<div className={classes.divider}>
						<div />
					</div>
					<FakeTable>
						<Row>
							<div className={classes.emphasizedText}>Total Price</div>
							<div>{NON_BREAKING_SPACE}</div>
							<div>{NON_BREAKING_SPACE}</div>
							<div>{NON_BREAKING_SPACE}</div>
							<div className={classes.emphasizedText}>
								{formatNumsToDollars(totalPriceWithPromo)}
							</div>
							<div>{NON_BREAKING_SPACE}</div>
						</Row>
					</FakeTable>
					<div>
						<Button
							onClick={handlePromoEntryDialogState}
							className={classes.addPromoButton}
							disabled={wizard_state['applied_promo'] !== null}
							variant="text">
							Add Promo
						</Button>
					</div>
				</div>
				<div className={classes.actionsContainer}>
					<DifferentFacilityBookingWarning
						facility={facility}
						defaultFacilityId={household?.default_facility_id}
						style={{ marginBottom: 16 }}
					/>
					<NoFacilityCardWarning
						facilityName={facilityCardStatus.facilityName || facility?.name}
						hasPaymentMethod={facilityCardStatus.hasPaymentMethod}
						loading={facilityCardStatus.loading}
						style={{ marginBottom: 16 }}
					/>
					<PaymentMethodCard
						raised={false}
						household={household}
						facility={facility}
						paymentMethods={facilityCardStatus.paymentMethods}
						onCardSaved={facilityCardStatus.refresh}
						makeResponsive
					/>
					<div className={classes.actionButtonContainer}>
						<MainButton
							onClick={handleOnConfirmAndBook}
							disabled={facilityCardStatus.loading || !hasCardAtThisFacility}
							variant="contained"
							label="Confirm & Book"
						/>
						<div className={classes.disclaimerText}>
							By clicking "Confirm & Book," I agree that I have notified the client of the quoted
							price, and
							<ButtonBase
								className={classes.inlineButtons}
								onClick={handleCancellationPolicyDialogState}>
								cancelation policy
							</ButtonBase>
							.
						</div>
						{showBankCreditDisclaimer ? (
							<StyledAlert
								type="info"
								text="Please note the invoice total may be adjusted if the client's daycare bank balance drops, their account credit is used or their enrichment bank is used prior to the completion of this visit."
								style={{ marginTop: 8 }}
							/>
						) : null}
					</div>
				</div>
			</div>
		</SectionFormContainer>
	)
}

export default ServiceSummaryAndPayment
